"use client";

/**
 * LinkedSessionBadge — surfaces "Artwork from Artwork Studio session: X" on
 * entity detail pages when an admin / creator is logged in. Per Gordon: "be
 * nice if the artist, album, track, event, market item showed that you
 * uploaded it to in artwork studio."
 *
 * Reverse-looks-up the `artworkSessions` collection by `linkedEntity.id` ==
 * the current entity id, picks the most recently updated session, and
 * renders a compact chip with the session label + a deep-link back to
 * /artwork-studio?session=<id> so one click reopens the same canvas in the
 * studio.
 *
 * Hidden for non-admin / non-creator viewers — public surfaces shouldn't
 * advertise internal session names.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { Sparkles } from "lucide-react";
import { collection, getDocs, query, where, limit, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { cn } from "@/lib/utils";

interface Props {
  entityId: string;
  /** Optional class merge for the outer pill */
  className?: string;
  /** When true, render only when a session is found (default). When false,
   *  render a "no linked session" placeholder so admins see the absence. */
  silentWhenMissing?: boolean;
}

type FoundSession = {
  id: string;
  name: string;
  label: string;
  thumbnailUrl: string | null;
};

export function LinkedSessionBadge({ entityId, className, silentWhenMissing = true }: Props) {
  const [session, setSession] = useState<FoundSession | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!entityId) { setLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        // The denormalised field on the session doc — written by Export To
        // Entity in artwork-studio/page.tsx. Index: linkedEntity.id + updatedAt.
        const q = query(
          collection(db, "artworkSessions"),
          where("linkedEntity.id", "==", entityId),
          orderBy("updatedAt", "desc"),
          limit(1),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        if (!snap.empty) {
          const d = snap.docs[0];
          const data = d.data() as any;
          setSession({
            id: d.id,
            name: data.name || "Untitled session",
            label: data?.linkedEntity?.label || "",
            thumbnailUrl: data.thumbnailDataUrl || null,
          });
        }
      } catch (err) {
        // Missing composite index is the most common error; we surface
        // nothing to the user but log so admins can read the hint in
        // Firestore that points at the index it wants.
        console.warn("[LinkedSessionBadge] lookup failed", err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [entityId]);

  if (!loaded) return null;
  if (!session) {
    if (silentWhenMissing) return null;
    return (
      <span className={cn("inline-flex items-center gap-1 text-[10px] text-muted-foreground/60", className)}>
        <Sparkles className="h-3 w-3" /> No linked Artwork Studio session
      </span>
    );
  }

  return (
    <Link
      href={`/artwork-studio?session=${encodeURIComponent(session.id)}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2 py-1",
        "text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors max-w-[260px]",
        className,
      )}
      title={`Open "${session.name}" in Artwork Studio`}
    >
      <Sparkles className="h-3 w-3 flex-shrink-0" />
      {session.thumbnailUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={session.thumbnailUrl}
          alt=""
          className="w-4 h-4 rounded object-cover flex-shrink-0"
        />
      )}
      <span className="truncate">Artwork: {session.name}</span>
    </Link>
  );
}
